import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getAppServices } from "../../client/services/services";
import { useSiteContext } from "../../state/SiteContext";
import { useAuth } from "../internal/AuthProvider";

interface ForgotPasswordProps {
    open: boolean;
    onClose: () => void;
    initialEmail?: string;
}

type ResetStep = "email" | "success";

const SEND_FAILURE = "We could not send the email. Wait a moment and try again.";

export default function ForgotPassword({ open, onClose, initialEmail = "" }: ForgotPasswordProps) {
    const { siteState } = useSiteContext();
    const { showLogin } = useAuth();
    const siteName = siteState.site?.name || "";
    const [email, setEmail] = useState(initialEmail);
    const [emailError, setEmailError] = useState("");
    const [error, setError] = useState("");
    const [step, setStep] = useState<ResetStep>("email");
    const [isLoading, setIsLoading] = useState(false);
    const services = getAppServices();

    useEffect(() => {
        if (open && initialEmail) {
            setEmail(initialEmail);
        }
    }, [open, initialEmail]);

    useEffect(() => {
        if (!open) {
            setEmail(initialEmail);
            setEmailError("");
            setError("");
            setStep("email");
            setIsLoading(false);
        }
    }, [open, initialEmail]);

    const validateEmail = (emailValue: string): boolean => {
        if (!emailValue.trim()) {
            setEmailError("Email is required");
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setEmailError("Please enter a valid email address");
            return false;
        }
        setEmailError("");
        return true;
    };

    const handleSendLink = async (event?: React.FormEvent) => {
        event?.preventDefault();
        if (!validateEmail(email)) {
            return;
        }
        if (!siteName) {
            setError(SEND_FAILURE);
            return;
        }
        setIsLoading(true);
        setError("");
        try {
            await services.auth.forgotPassword(email, siteName);
            setStep("success");
        } catch (sendError: unknown) {
            const status = typeof sendError === "object" && sendError !== null && "status" in sendError
                ? Number((sendError as { status: number }).status)
                : 0;
            const message = sendError instanceof Error ? sendError.message : "";
            setError(status === 429 ? message : SEND_FAILURE);
        } finally {
            setIsLoading(false);
        }
    };

    const emailStep = (
        <Box component="form" className="forgot-password-form" onSubmit={handleSendLink}>
            <Typography variant="h6" component="h2" className="forgot-password-title">Reset password</Typography>
            <Typography className="forgot-password-description">
                Enter your email. If a local account exists, we will send a link to set a password.
            </Typography>
            {error && <Typography color="error" className="forgot-password-error">{error}</Typography>}
            <TextField
                margin="normal"
                required
                fullWidth
                id="forgot-email"
                label="Email"
                type="email"
                autoFocus
                value={email}
                onChange={(event) => {
                    setEmail(event.target.value);
                    if (emailError) {
                        validateEmail(event.target.value);
                    }
                }}
                onBlur={() => validateEmail(email)}
                error={!!emailError}
                helperText={emailError}
                disabled={isLoading}
            />
            <Button
                type="submit"
                fullWidth
                variant="contained"
                className="forgot-password-button"
                disabled={isLoading || !!emailError || !email.trim()}
            >
                {isLoading ? "Sending..." : "Send link"}
            </Button>
            <Button
                fullWidth
                variant="text"
                onClick={() => { onClose(); showLogin(); }}
                className="forgot-password-button-back"
            >
                Back to login
            </Button>
        </Box>
    );

    const successStep = (
        <Box className="forgot-password-center">
            <Typography variant="h6" component="h2" className="forgot-password-success-title">Check your email</Typography>
            <Typography className="forgot-password-success-text">
                If an account exists for that address, we sent a link to set a password. The link works once and expires in 45 minutes.
            </Typography>
            <Button
                fullWidth
                variant="text"
                onClick={() => { onClose(); showLogin(); }}
                className="forgot-password-button-back"
            >
                Back to login
            </Button>
        </Box>
    );

    return (
        <Modal open={open} onClose={onClose}>
            <Box className="login-container forgot-password-container">
                {step === "email" ? emailStep : successStep}
            </Box>
        </Modal>
    );
}
